import { store } from '../../../../app/store';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import { gridApi } from '../../api/gridApi';
import type { CommandHandler } from '../types';

// Temporary fallback until backend exposes RedirectUrl in command settings
const FALLBACK_INVOICE_DETAIL_URL = '/finances/invoices/output/';

export const generateAutoInvoiceCommandHandler: CommandHandler = async (command, context) => {
  const settingsId = Number(context.entityId ?? context.parentId);
  if (!Number.isInteger(settingsId) || settingsId <= 0) {
    return {
      success: false,
      error: 'Save the record first before generating an invoice.',
    };
  }

  const data = context.entityData ?? {};

  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

  const rawLogStart = typeof data.PeriodStartDate === 'string' ? data.PeriodStartDate.trim() : '';
  const rawLogEnd = typeof data.PeriodEndDate === 'string' ? data.PeriodEndDate.trim() : '';
  const rawDateOfCreate =
    typeof data.InvoiceCreateDate === 'string' ? data.InvoiceCreateDate.trim() : '';
  const logStart = rawLogStart || firstOfMonth;
  const logEnd = rawLogEnd || now.toISOString();
  const dateOfCreate = rawDateOfCreate || now.toISOString();
  const rawRate = data.ExchangeRate;
  const exchangeRate =
    rawRate !== undefined && rawRate !== null && String(rawRate).trim() !== ''
      ? Number(rawRate)
      : null;

  try {
    const result = await store
      .dispatch(
        gridApi.endpoints.generateAutoInvoice.initiate({
          SettingsId: settingsId,
          LogStart: logStart,
          LogEnd: logEnd,
          DateOfCreate: dateOfCreate,
          ExchangeRate: exchangeRate,
        })
      )
      .unwrap();

    // Use RedirectUrl from command settings when backend exposes it,
    // fall back to hardcoded path until then
    const redirectUrl =
      typeof command.settings === 'string' && command.settings.trim()
        ? command.settings
        : FALLBACK_INVOICE_DETAIL_URL;

    const failedWarning =
      result.failedEmployees.length > 0
        ? `\nFailed employees: ${result.failedEmployees.join(', ')}`
        : '';

    // No invoices created — just show message, no navigation dialog
    if (result.invoicesCreated === 0 || result.invoiceId == null) {
      return {
        success: true,
        message: (result.message || 'No invoices were created.') + failedWarning,
      };
    }

    // Invoice created — return modal payload so UI can ask user to navigate
    return {
      success: true,
      autoInvoiceModal: {
        invoiceId: result.invoiceId,
        redirectUrl,
        failedEmployees: result.failedEmployees,
        message: result.message + failedWarning,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to generate invoice.'),
    };
  }
};
